import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { WebAddresses } from '../app.config';
import { globalUser } from '../helpers/globalFields';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openSnackBar } from '../helpers/genericFunctions';
import { ApiResponse } from './userpermission.service';

export interface Organization {
  id: number;
  org_name: string;
  createdAt?: string;
  userCount?: number;
}

export interface GlobalUser {
  usr_id: number;
  usr_name: string;
  usr_mail: string;
  createdAt: string;
  permissions: { entry_id: number; key: string }[];
}

/**
 * Service for superadmin operations:
 * - Organization CRUD
 * - Global user management (users with org-independent permissions)
 * - Assigning orphaned users to organizations
 * - Resending invite links
 *
 * All endpoints require the `system.admin` permission.
 *
 * @author julian.f.verhey
 */
@Injectable({
  providedIn: 'root',
})
export class SuperadminService {
  private base = WebAddresses.TicketAuthorisation;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {}

  private headers() {
    return { headers: globalUser.getHeaders() };
  }

  // ── Organizations ──────────────────────────────────────────────────────────

  /**
   * Get all organizations.
   */
  getOrganizations(): Observable<ApiResponse<Organization[]>> {
    return this.http
      .get<
        ApiResponse<Organization[]>
      >(`${this.base}/superadmin/organizations/findall`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Create a new organization.
   * @param org_name - Unique organization name
   */
  createOrganization(org_name: string): Observable<ApiResponse<Organization>> {
    return this.http
      .post<
        ApiResponse<Organization>
      >(`${this.base}/superadmin/organizations/create`, { org_name }, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Delete an organization by ID.
   * This will CASCADE-delete all org-scoped permission rows.
   * @param orgId - Organization primary key
   */
  deleteOrganization(orgId: number): Observable<ApiResponse<any>> {
    return this.http
      .delete<
        ApiResponse<any>
      >(`${this.base}/superadmin/organizations/delete/${orgId}`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  // ── Global users ───────────────────────────────────────────────────────────

  /**
   * Get all users whose permissions have upm_organization_id = NULL.
   * These are "global" users not bound to any org.
   */
  getGlobalUsers(): Observable<ApiResponse<GlobalUser[]>> {
    return this.http
      .get<
        ApiResponse<GlobalUser[]>
      >(`${this.base}/superadmin/globalusers/findall`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Revoke a specific global permission entry by its userpermission PK.
   * @param entryId - userpermission row PK
   */
  revokeGlobalPermission(entryId: number): Observable<ApiResponse<any>> {
    return this.http
      .delete<
        ApiResponse<any>
      >(`${this.base}/superadmin/globalusers/permission/delete/${entryId}`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Assign an existing user to an organization by creating a permission entry.
   * Used to re-attach orphaned global users to an org.
   *
   * @param userId     - User primary key
   * @param orgId      - Target organization primary key
   * @param permissionIds - Permission IDs to assign in that org context
   */
  assignUserToOrganization(
    userId: number,
    orgId: number,
    permissionIds: number[],
  ): Observable<ApiResponse<any>> {
    return this.http
      .post<
        ApiResponse<any>
      >(`${this.base}/superadmin/globalusers/${userId}/assign`, { org_id: orgId, permission_ids: permissionIds }, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  // ── Invites ────────────────────────────────────────────────────────────────

  /**
   * Resend an invite to a pending user (one with a non-null usr_INVITE_TOKEN).
   * Generates a fresh 48h token and re-emails the activation link.
   *
   * @param userId - User primary key of the pending user
   */
  resendInvite(
    userId: number,
  ): Observable<ApiResponse<{ inviteLink: string }>> {
    return this.http
      .post<
        ApiResponse<{ inviteLink: string }>
      >(`${this.base}/superadmin/invite/${userId}/resend`, {}, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  /**
   * Get all pending users (usr_INVITE_TOKEN is not null / usr_ACTIVE is false).
   */
  getPendingUsers(): Observable<ApiResponse<any[]>> {
    return this.http
      .get<
        ApiResponse<any[]>
      >(`${this.base}/superadmin/pending`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  getUsersForOrg(orgId: number): Observable<ApiResponse<any[]>> {
    return this.http
      .get<
        ApiResponse<any[]>
      >(`${this.base}/superadmin/organizations/${orgId}/users`, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  getDanglingUsers(): Observable<ApiResponse<any[]>> {
    return this.http
      .get<
        ApiResponse<any[]>
      >(`${this.base}/superadmin/danglingusers`, this.headers())
      .pipe(
        tap((r) => {
          if (r.status !== 'success')
            openSnackBar(r.message, 5000, this.snackBar);
        }),
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  getUsersNotInOrg(orgId: number): Observable<ApiResponse<any[]>> {
    return this.http
      .get<
        ApiResponse<any[]>
      >(`${this.base}/superadmin/organizations/${orgId}/available-users`, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  deleteUser(userId: number): Observable<ApiResponse<any>> {
    return this.http
      .delete<
        ApiResponse<any>
      >(`${this.base}/superadmin/users/${userId}`, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  deletePendingUser(userId: number): Observable<ApiResponse<any>> {
    return this.http
      .delete<
        ApiResponse<any>
      >(`${this.base}/superadmin/pending/${userId}`, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  getAllUsers(): Observable<ApiResponse<any[]>> {
    return this.http
      .get<ApiResponse<any[]>>(`${this.base}/superadmin/users`, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  toggleUserActive(
    userId: number,
    active: boolean,
  ): Observable<ApiResponse<any>> {
    return this.http
      .patch<
        ApiResponse<any>
      >(`${this.base}/superadmin/users/${userId}/active`, { active }, this.headers())
      .pipe(
        catchError((e) => {
          console.error(e);
          return throwError(() => e);
        }),
      );
  }

  revokeAllGlobalPermissions(userId: number): Observable<ApiResponse<any>> {
  return this.http
    .delete<ApiResponse<any>>(`${this.base}/superadmin/users/${userId}/global-permissions`, this.headers())
    .pipe(catchError((e) => { console.error(e); return throwError(() => e); }));
}
}
